import { useResultBuffer } from '@/hooks/useResultBuffer';
import uniqueId from 'lodash/uniqueId';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';

import { ClickableLetterSquareContainer } from '../ClickableLetterSquare/container';
import { FeedBack } from '../FeedBack';
import { FullKeyboard } from '../FullKeyboard';
import { LetterSquareContainer } from '../LetterSquare/container';
import OfflineImage from '../SharedComponents/OfflineImage/OfflineImage';
import { OfflineWrapper } from '@/layouts/OfflineWrapper';
import { VideoPlayer } from '../Video';
import ResponsiveText from '@/components/SharedComponents/ResponsiveText/ResponsiveText';
import { hasOwnProperty } from '@/utils/utils';
import { styles } from './styles';

//method to fix toUpperCase turning ß into SS
// eslint-disable-next-line no-extend-native
String.prototype.toFixedUppercase = function () {
  let upperCaseString = '';
  for (let ii = 0; ii < ('' + this).length; ii++) {
    if ('' + this[ii] !== 'ß') {
      upperCaseString = upperCaseString + '' + this[ii].toUpperCase();
    } else {
      upperCaseString = upperCaseString + '' + this[ii];
    }
  }
  return upperCaseString;
};

function findAllIndexes(str, letter) {
  const indexes = [];
  for (let i = 0; i < str.length; i++) {
    if (str[i] === letter) {
      indexes.push(i);
    }
  }
  return indexes;
}

function isWordFilled(arr, emptySpaces) {
  if (emptySpaces.length === 0) {
    return arr.every((eachElem) => eachElem !== null);
  }
  const filteredArr = arr.filter((eachElem, index) => {
    return !emptySpaces.includes(index);
  });
  return filteredArr.every((eachElem) => eachElem !== null);
}

let mistakes = {};

const generateExerciseType = (setting, name) => {
  return {
    ...setting,
    name,
  };
};

const generateSetItemType = (key) => `${key}setitem`;

export function WritingExercise({
  isSolved,
  exercise,
  settings,
  currentExercise,
  peek,
  patientId,
  isSelf,
  next,
}) {
  const { setting, value, playbackRate } = settings;
  const { shuffle, word, gap, video } = exercise;

  const correctImage = exercise?.retinaImage ?? exercise?.correct?.[0];
  const pattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
  const isNumberWord = pattern.test(word);

  const [currentOrder, setCurrentOrder] = useState(0);
  const currentOrderAsKey = String(currentOrder);
  const [guessedLetters, setGuessedLetters] = useState([]);
  const [correctWord, setCorrectWord] = useState(
    new Array(word?.length).fill(null),
  );
  const setItemType = generateSetItemType(exercise.exerciseClass);
  const emptySpaces = useMemo(() => findAllIndexes(word, ' '), [word]);
  const allCorrect = useMemo(() => {
    return isWordFilled(correctWord, emptySpaces);
  }, [correctWord, emptySpaces]);
  const [wrongBoxChosen, setWrongBoxChosen] = useState(0);

  const consecutiveWrongBoxChosen = useCallback((reset = false) => {
    if (reset) {
      setWrongBoxChosen(0);
    } else {
      setWrongBoxChosen((prev) => prev + 1);
    }
  }, []);

  const { resultCreator } = useResultBuffer({
    setItemType,
    setItemTypeId: exercise.wordsetitem,
    exerciseType: generateExerciseType(settings, exercise?.type),
    patientId,
    shouldTracksMistakes: true,
    detailedPerformance: true,
    isSelf,
  });

  const isFullKeyboard = setting === 'full';
  const isGap = setting === 'gap';
  const isCurrentLetterUpperCase = (currentIndex) => {
    if (currentIndex < word.length) {
      // If the currentIndex is out of bounds
      if (!word[currentIndex]) {
        return false;
      }
      return word[currentIndex] === word?.[currentIndex]?.toUpperCase();
    }
    return false;
  };

  const resetState = useCallback((word) => {
    // ? Info -> this function is responsible for reseting the state if the exercise is skipped or solved
    setCurrentOrder(0);
    setGuessedLetters([]);
    setCorrectWord(new Array(word?.length).fill(null));
    mistakes = {};
  }, []);

  const splitLetters = useMemo(() => {
    const shuffled = shuffle.split('').map((letter, index) => {
      const includedIndexes = findAllIndexes(
        word.toLowerCase(),
        letter.toLowerCase(),
      );
      const order = includedIndexes.length === 0 ? -1 : includedIndexes;
      const returnedObj = {
        order,
        capitalLetter: letter.toFixedUppercase(),
        letter: letter.toLowerCase(),
        i: index,
        key: uniqueId(),
      };
      return returnedObj;
    });
    if (emptySpaces.length === 0) {
      return shuffled;
    }
    return shuffled.filter((eachShuffle) => {
      return eachShuffle.letter !== ' ';
    });
  }, [shuffle, word, guessedLetters.length]);

  const onCorrect = useCallback(
    (id, currentOrder, letter) => {
      consecutiveWrongBoxChosen(true);
      setCorrectWord((prevState) => {
        const clonedState = [...prevState];
        clonedState[currentOrder] = letter;
        return clonedState;
      });

      setCurrentOrder((prevState) => {
        const newState = prevState + 1;
        if (emptySpaces.includes(newState)) {
          return newState + 1;
        }
        return newState;
      });

      if (id === undefined) {
        return;
      }
      setTimeout(() => {
        setGuessedLetters((prevState) => [...prevState, id]);
      }, 500);
    },
    [emptySpaces.toString()],
  );

  const onCorrectIsGap = useCallback(
    (id, currentOrder, letter) => {
      consecutiveWrongBoxChosen(true);
      setCorrectWord((prevState) => {
        const clonedState = [...prevState];
        clonedState[currentOrder] = letter;
        setCurrentOrder(() => {
          const newState = clonedState.findIndex((eachGap) => eachGap === null);
          if (emptySpaces.includes(newState)) {
            return newState + 1;
          }
          return newState;
        });
        return clonedState;
      });
      setTimeout(() => {
        setGuessedLetters((prevState) => [...prevState, id]);
      }, 500);
    },
    [emptySpaces.toString()],
  );

  const onWrong = useCallback(
    (i) => {
      consecutiveWrongBoxChosen(false);
      if (hasOwnProperty(mistakes, currentOrderAsKey)) {
        if (mistakes[currentOrderAsKey].includes(i)) {
          return;
        }
        mistakes[currentOrderAsKey].push(i);
      } else {
        mistakes[currentOrderAsKey] = [i];
      }
    },
    [currentOrderAsKey],
  );

  useEffect(() => {
    resetState(word);
  }, [word]);

  useEffect(() => {
    if (!isGap) {
      return;
    }
    if (value >= word.length) {
      return;
    }
    const newCorrectWord = gap.map((eachGap, i) => {
      const shouldBeReplaced = !!eachGap;
      if (shouldBeReplaced) {
        return word[i];
      }
      if (emptySpaces.includes(i)) {
        return '';
      }
      return null;
    });
    const newCurrentOrder = newCorrectWord.findIndex(
      (eachGap) => eachGap === null,
    );
    setCorrectWord(newCorrectWord);
    setCurrentOrder(newCurrentOrder);
  }, [currentExercise, word, gap?.join(''), emptySpaces.toString()]);

  useEffect(() => {
    if (!allCorrect) {
      return;
    }
    const mistakesNumbered = Object.values(mistakes).flat().length;
    resultCreator(mistakesNumbered);
    if (!isSelf) {
      isSolved();
    }
  }, [allCorrect]);

  return (
    <View style={styles.flex1}>
      <LetterSquareContainer
        emptySpaces={emptySpaces}
        correctWord={correctWord}
      />
      <View style={styles.exerciseArea}>
        <View style={styles.imageWrapper}>
          <View style={styles.imageInner}>
            <OfflineImage
              style={styles.fill}
              source={{ uri: correctImage }}
              resizeMode="contain"
            />
            {allCorrect ? <FeedBack status="correct" /> : null}
          </View>
        </View>

        {isSelf ? (
          <View style={styles.flex1Relative}>
            <OfflineWrapper url={video}>
              <VideoPlayer
                noVideo
                correctSelected={allCorrect}
                onCorrect={() => next()}
                playbackRate={playbackRate}
              >
                <ResponsiveText style={styles.exerciseText}>
                  {word}
                </ResponsiveText>
              </VideoPlayer>
            </OfflineWrapper>
          </View>
        ) : (
          <View style={styles.flex2}>
            {!!peek && (
              <ResponsiveText style={styles.exerciseText}>
                {word}
              </ResponsiveText>
            )}
          </View>
        )}
      </View>
      <View style={styles.keyboardArea}>
        {isFullKeyboard ? (
          <FullKeyboard
            currentOrder={currentOrder}
            splitLetters={splitLetters}
            onCorrect={onCorrect}
            onWrong={onWrong}
            word={word}
            isNumberWord={isNumberWord}
            isWordset
            capitalizeAll={isCurrentLetterUpperCase(currentOrder)}
            wrongBoxChosen={!isSelf ? 0 : wrongBoxChosen}
          />
        ) : (
          <ClickableLetterSquareContainer
            isCapitallizeAll={isCurrentLetterUpperCase(currentOrder)}
            currentOrder={currentOrder}
            splitLetters={splitLetters}
            guessedLetters={guessedLetters}
            onCorrect={isGap ? onCorrectIsGap : onCorrect}
            onWrong={onWrong}
            word={word}
            wrongBoxChosen={!isSelf ? 0 : wrongBoxChosen}
          />
        )}
      </View>
    </View>
  );
}
